import { h } from 'preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { memo } from 'preact/compat';

import { useSelector, useDispatch } from 'react-redux';

import { styled } from '@mui/material/styles';

import List from '@mui/material/List';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ListSubheader from '@mui/material/ListSubheader';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Restore from '@mui/icons-material/Restore.js';

import MuiDrawer from '@mui/material/Drawer';

import ReactiveItemLink from '../ListLink/reactiveItemLink.jsx';

import { actions as drawerActions } from '../../store/state/changesDrawer.js';
import { v2Endpoints } from '../../apis/kappa.js';
import { fullDayFormatter } from '../../utils/dateFormatters.js';
import { makeSimpleLazyList } from '../lazyList/lazyList.jsx';
import increase from '../../utils/increase.js';
import { PUBLIC_FOLDER } from '../../consts.js';

const DRAWER_WIDTH = 480;

function getDrawerState(state) {
  return state.changesDrawer;
}

function getSelectedEndpoint(state) {
  return state.selectedEndpoint;
}

const Drawer = styled(
  MuiDrawer,
  {}
)(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    whiteSpace: 'nowrap',
    width: DRAWER_WIDTH,
    maxWidth: '100vw',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
  },
  ...(!open && {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  }),
}));

function LazyChange(_props) {
  return (
    <ListItem>
      <Stack
        direction={'row'}
        flexGrow={1}
        alignItems={'center'}
        maxWidth={'100%'}
      >
        <ListItemText
          sx={{ flexGrow: 1 }}
          primary={<Skeleton height={24} width={'100%'} />}
          secondary={<Skeleton height={40} width={'100%'} />}
        />
        <Skeleton height={48} width={48} />
      </Stack>
    </ListItem>
  );
}

function Change(props) {
  const { model } = props;

  return (
    <ListItem>
      <ReactiveItemLink href={`/v2/change/${model.originalId}/${model._id}`}>
        <Stack
          direction={'row'}
          flexGrow={1}
          alignItems={'center'}
          maxWidth={'100%'}
        >
          <ListItemText
            sx={{ flexGrow: 1 }}
            primary={
              <>
                <Typography
                  sx={{ display: 'inline' }}
                  component="span"
                  variant="body1"
                >
                  Author:{' '}
                </Typography>
                <Typography
                  sx={{ display: 'inline' }}
                  component="span"
                  variant="body2"
                >
                  {model.author}
                </Typography>
              </>
            }
            secondary={
              <>
                <Typography
                  sx={{ display: 'inline' }}
                  component="span"
                  variant="body2"
                >
                  Created{' '}
                </Typography>
                {fullDayFormatter(model.created)}
                <br />
                {model._id}
              </>
            }
          />
          <IconButton
            component="a"
            href={`${PUBLIC_FOLDER}/v2/change/${model.originalId}/${model._id}`}
            target="_blank"
            aria-label="Restore"
            sx={{ width: 48, height: 48 }}
          >
            <Restore />
          </IconButton>
        </Stack>
      </ReactiveItemLink>
    </ListItem>
  );
}

function FakeChange(props) {
  const { model } = props;

  return (
    <ListItem>
      <ReactiveItemLink href={`/v2/edit/${model._id}`}>
        <Stack
          direction={'row'}
          flexGrow={1}
          alignItems={'center'}
          maxWidth={'100%'}
        >
          <ListItemText
            sx={{ flexGrow: 1 }}
            primary={
              <>
                <Typography
                  sx={{ display: 'inline' }}
                  component="span"
                  variant="body1"
                >
                  Author:{' '}
                </Typography>
                <Typography
                  sx={{ display: 'inline' }}
                  component="span"
                  variant="body2"
                >
                  {model.lastAuthor}
                </Typography>
              </>
            }
            secondary={
              <>
                <Typography
                  sx={{ display: 'inline' }}
                  component="span"
                  variant="body2"
                >
                  Created{' '}
                </Typography>
                {fullDayFormatter(model.lastUpdated)}
                <br />
                {model._id}
              </>
            }
          />
          <IconButton
            component="a"
            href={`${PUBLIC_FOLDER}/v2/edit/${model._id}`}
            target="_blank"
            aria-label="Restore"
            sx={{ width: 48, height: 48 }}
          >
            LIVE
          </IconButton>
        </Stack>
      </ReactiveItemLink>
    </ListItem>
  );
}

function getEndpointList(store) {
  return store.endpoints.list.v2;
}

function ChangesSidebar(_props) {
  const dispatch = useDispatch();
  const open = useSelector(getDrawerState);
  const selectedEndpoint = useSelector(getSelectedEndpoint);
  const endpoints = useSelector(getEndpointList);
  const [restartCount, setRestartCount] = useState(0);

  const closeDrawer = useCallback(
    function closeDrawer() {
      dispatch(drawerActions.CLOSE());
    },
    [dispatch]
  );

  useEffect(
    function reactToOpen() {
      setRestartCount(increase);
    },
    [open]
  );

  const LazyList = useMemo(
    function prepareLazyList() {
      function getItems(offset, limit) {
        if (!selectedEndpoint.endpointId) {
          return Promise.resolve([]);
        }

        return v2Endpoints.listVersions(
          selectedEndpoint.endpointId,
          limit,
          offset
        );
      }

      const LazyList = makeSimpleLazyList(getItems, 20, LazyChange, Change, {});

      return LazyList;
    },
    [selectedEndpoint.endpointId]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={closeDrawer}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <Box sx={{ px: 3, py: 2 }}>
        <List
          subheader={
            <Stack direction={'row'} alignItems={'center'}>
              <ListSubheader sx={{ px: 0, flexGrow: 1 }}>
                <Typography variant="body1">Changes for Endpoint</Typography>
                <Typography
                  variant="small"
                  lineHeight={1}
                  style={{
                    whiteSpace: 'break-spaces',
                    lineHeight: '12px !important',
                  }}
                >
                  <p>
                    Changes are kept for 90 days, and are created from the
                    currently saved on database right before you modify an
                    existing endpoint
                  </p>
                </Typography>
              </ListSubheader>
            </Stack>
          }
        >
          {selectedEndpoint.model && (
            <FakeChange model={selectedEndpoint.model} />
          )}
          <LazyList restartCount={restartCount} />
        </List>
      </Box>
    </Drawer>
  );
}

export default memo(ChangesSidebar);
